import { isAxiosError } from "axios"
import { useCallback, useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"
import useSWR from "swr"
import type { Pagination, Repayment, RepaymentFormValues, RepaymentListItem } from "../types"
import { toDateTimeOrNull, toStringOrNull } from "src/features/admin/screen-shared"

const ENDPOINT = `${SMOOTH_SMOOTHER_API_URL}/screen/repayments`

export const useRepayments = (page: number) => {
  const { getWithToken } = useSmoothApi()
  const { data, error, isLoading } = useSWR(
    `${ENDPOINT}?page=${page}`,
    async (url: string) =>
      (await getWithToken(url)).data as { repayments: RepaymentListItem[]; pagination: Pagination },
    { revalidateOnFocus: false }
  )

  return {
    repayments: data?.repayments ?? [],
    pagination: data?.pagination ?? null,
    error: error ?? null,
    isLoading,
  }
}

export const useRepayment = (id: number | null) => {
  const { getWithToken } = useSmoothApi()
  const { data, error, isLoading, mutate } = useSWR(
    id ? `${ENDPOINT}/${id}` : null,
    async (url: string) => (await getWithToken(url)).data as { repayment: Repayment },
    { revalidateOnFocus: false }
  )

  return { repayment: data?.repayment ?? null, error: error ?? null, isLoading, mutate }
}

const toMessage = (e: unknown, fallback: string) =>
  isAxiosError(e) && typeof e.response?.data?.message === "string"
    ? e.response.data.message
    : fallback

const toRequestBody = (values: RepaymentFormValues) => ({
  reconciliationId: toStringOrNull(values.reconciliationId),
  amount: toStringOrNull(values.amount),
  paymentAt: toDateTimeOrNull(values.paymentAt),
  paymentStatus: values.paymentStatus,
})

type SaveResult<T> = { ok: true; value: T } | { ok: false; message: string }

export const useSaveRepayment = () => {
  const { postWithToken, patchWithToken, deleteWithToken } = useSmoothApi()
  const [isSaving, setIsSaving] = useState(false)

  const run = useCallback(
    async <T>(request: () => Promise<T>, fallback: string): Promise<SaveResult<T>> => {
      setIsSaving(true)
      try {
        return { ok: true, value: await request() }
      } catch (e) {
        return { ok: false, message: toMessage(e, fallback) }
      } finally {
        setIsSaving(false)
      }
    },
    []
  )

  const create = useCallback(
    (values: RepaymentFormValues) =>
      run(async () => {
        const response = await postWithToken(ENDPOINT, toRequestBody(values))
        return (response.data as { id: number }).id
      }, "作成に失敗しました"),
    [postWithToken, run]
  )

  const update = useCallback(
    (id: number, values: RepaymentFormValues) =>
      run(() => patchWithToken(`${ENDPOINT}/${id}`, toRequestBody(values)), "保存に失敗しました"),
    [patchWithToken, run]
  )

  const destroy = useCallback(
    (id: number) => run(() => deleteWithToken(`${ENDPOINT}/${id}`), "削除に失敗しました"),
    [deleteWithToken, run]
  )

  return { create, update, destroy, isSaving }
}
